import { FlatList, ListRenderItemInfo, Pressable, StyleSheet, View, ViewProps } from 'react-native'
import { useWorkspaceTypeElements, useWorkspaceTypes, WorkspaceType, WorkspaceTypeElement } from 'zens-store'
import { Spacing, Text, Icon, Color } from 'zens-storybook'
import { useState } from 'react'
import { Trans } from 'react-i18next'

import FloorplanAsset from './asset'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  row: {
    paddingHorizontal: Spacing.M,
    paddingVertical: Spacing.S,
    flexDirection: 'row',
    alignItems: 'center',
  },
  selected: {
    backgroundColor: Color.lightGreyBlue,
  },
  right: {
    marginLeft: Spacing.XS,
  },
  element: {
    flex: 1,
    marginLeft: Spacing.XS,
  },
  elementSelected: {
    color: Color.darkGreyBlue,
  },
  header: {
    paddingHorizontal: Spacing.M,
    paddingVertical: Spacing.S,
    color: Color.metalGrey,
  },
})

interface Props extends ViewProps {
  workspaceTypeId?: number
  elementId?: number
  onSelect?: (element?: WorkspaceTypeElement) => void
}

const FloorplanElements = (props: Props) => {
  const { workspaceTypeId, elementId, onSelect, style, ...otherProps } = props
  const [unfolded, setUnfolded] = useState<number>()
  const elements = useWorkspaceTypeElements()
  const workspaceTypes = useWorkspaceTypes()

  const data = workspaceTypes.data?.filter((item) => !workspaceTypeId || item.id === workspaceTypeId)
  function select(workspaceType: WorkspaceType) {
    if (unfolded === workspaceType.id) {
      setUnfolded(undefined)
      onSelect?.()
    } else {
      setUnfolded(workspaceType.id)
      const workspaceTypeElements = elements.data?.filter((item) => item.workspaceTypeId === workspaceType.id)
      if (workspaceTypeElements?.length === 1) onSelect?.(workspaceTypeElements[0])
    }
  }

  function renderElements(workspaceType?: WorkspaceType) {
    const workspaceElements = elements.data?.filter((element) => element.workspaceTypeId === workspaceType?.id)
    return workspaceElements?.map((element) => {
      const selected = element.id === elementId
      return (
        <Pressable
          onPress={() => onSelect?.(element)}
          key={element.id}
          {...otherProps}
          testID="FloorplanElements.Element"
        >
          <View style={[styles.row, selected && styles.selected]}>
            <FloorplanAsset uri={element.src} height={20} width={35} />
            <Text level={5} style={[styles.element, selected && styles.elementSelected]}>
              {element.name}
            </Text>
          </View>
        </Pressable>
      )
    })
  }

  function renderRow(info: ListRenderItemInfo<WorkspaceType>) {
    const { item: workspaceType } = info
    const { name, id } = workspaceType
    if (workspaceTypeId && data?.length === 1) return <>{renderElements(workspaceType)}</>

    return (
      <>
        <Pressable onPress={() => select(workspaceType)} {...otherProps} testID="FloorplanElements.Row">
          <View style={styles.row}>
            <View style={styles.content}>
              <Text>{name}</Text>
            </View>
            <Icon name={unfolded === id ? 'down' : 'right'} style={styles.right} size="XS" />
          </View>
        </Pressable>
        {unfolded === id && renderElements(workspaceType)}
      </>
    )
  }

  function renderHeader() {
    const isSingle =
      data?.length === 1 &&
      elements.data?.filter((element) => element.workspaceTypeId === workspaceTypes.data?.[0]?.id).length === 1
    return (
      <Text style={styles.header}>
        {isSingle ? (
          <Trans>Click on floorplan to position the workspace</Trans>
        ) : (
          <Trans>Choose a type and click on floorplan to position the workspace</Trans>
        )}
      </Text>
    )
  }

  return (
    <FlatList data={data} renderItem={renderRow} style={[styles.container, style]} ListHeaderComponent={renderHeader} />
  )
}

export default FloorplanElements
